import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import { conversationService } from '../../services/conversationService'
import { Modal } from '../ui/Modal'
import { RenameConversationModal } from './RenameConversationModal'
import { useToast } from '../../hooks/useToast'

export const ConversationHistory = forwardRef(({ onSelectConversation }, ref) => {
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, conversationId: null, title: '' })
  const [isDeleting, setIsDeleting] = useState(false)
  const [renameModal, setRenameModal] = useState({ isOpen: false, conversationId: null, title: '' })
  const [isRenaming, setIsRenaming] = useState(false)
  const [openMenuId, setOpenMenuId] = useState(null)
  const { showToast } = useToast()
  const menuRef = useRef(null)

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true)
        setError(null)

        const result = await conversationService.fetchUserConversations()

        if (result.error) {
          setError(result.error)
          setConversations([])
        } else {
          const formattedConversations = conversationService.formatConversationsForDisplay(
            result.conversations
          )
          setConversations(formattedConversations)
        }
      } catch (err) {
        setError('Failed to load conversations')
        setConversations([])
      } finally {
        setLoading(false)
      }
    }

    fetchConversations()
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null)
      }
    }

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [openMenuId])

  // Expose method to add new conversation
  useImperativeHandle(ref, () => ({
    addNewConversation: (conversationId) => {
      // Check if conversation already exists
      const exists = conversations.some((conv) => conv.id === conversationId)
      if (!exists) {
        const newConversation = {
          id: conversationId,
          title: 'New Conversation',
          timeAgo: 'Just now',
          lastMessageAt: new Date().toISOString(),
        }
        setConversations((prev) => [newConversation, ...prev])
      }
    },
  }))

  const handleConversationClick = (conversationId) => {
    if (onSelectConversation) {
      onSelectConversation(conversationId)
    }
  }

  const handleMenuToggle = (e, conversationId) => {
    e.stopPropagation() // Prevent triggering conversation click
    setOpenMenuId(openMenuId === conversationId ? null : conversationId)
  }

  const handleDeleteClick = (e, conversation) => {
    e.stopPropagation() // Prevent triggering conversation click
    setOpenMenuId(null) // Close menu
    setDeleteModal({
      isOpen: true,
      conversationId: conversation.id,
      title: conversation.title,
    })
  }

  const handleRenameClick = (e, conversation) => {
    e.stopPropagation() // Prevent triggering conversation click
    setOpenMenuId(null) // Close menu
    setRenameModal({
      isOpen: true,
      conversationId: conversation.id,
      title: conversation.title,
    })
  }

  const handleConfirmDelete = async () => {
    if (!deleteModal.conversationId) return

    setIsDeleting(true)
    try {
      const response = await fetch(`/api/conversations/${deleteModal.conversationId}/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await conversationService.getAuthToken()}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to delete conversation')
      }

      // Remove conversation from local state
      setConversations((prev) => prev.filter((c) => c.id !== deleteModal.conversationId))

      // Clear localStorage if this was the active conversation
      const currentConvId = localStorage.getItem('et_ai_conversation_id')
      if (currentConvId === deleteModal.conversationId) {
        conversationService.clearAllConversationData()
        // Reload the page to reset the chat
        window.location.reload()
      }

      showToast('Conversation deleted successfully', 'success')
      setDeleteModal({ isOpen: false, conversationId: null, title: '' })
    } catch (error) {
      console.error('Error deleting conversation:', error)
      showToast(error.message || 'Failed to delete conversation', 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, conversationId: null, title: '' })
  }

  const handleConfirmRename = async (newTitle) => {
    if (!renameModal.conversationId || !newTitle.trim()) return

    setIsRenaming(true)
    try {
      const response = await fetch(`/api/conversations/${renameModal.conversationId}/rename`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await conversationService.getAuthToken()}`,
        },
        body: JSON.stringify({
          newTitle: newTitle.trim(),
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to rename conversation')
      }

      // Update conversation in local state
      setConversations((prev) =>
        prev.map((c) =>
          c.id === renameModal.conversationId ? { ...c, title: newTitle.trim() } : c
        )
      )

      showToast('Conversation renamed successfully', 'success')
      setRenameModal({ isOpen: false, conversationId: null, title: '' })
    } catch (error) {
      console.error('Error renaming conversation:', error)
      showToast(error.message || 'Failed to rename conversation', 'error')
    } finally {
      setIsRenaming(false)
    }
  }

  const handleCancelRename = () => {
    setRenameModal({ isOpen: false, conversationId: null, title: '' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-400">Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-red-400">{error}</p>
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-500">No previous conversations</p>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-2">
        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            data-conversation-id={conversation.id}
            onClick={() => handleConversationClick(conversation.id)}
            className="group p-3 bg-dark-secondary border border-dark-tertiary rounded-lg cursor-pointer transition-all duration-200 hover:bg-dark-tertiary hover:border-gray-500 relative hover-trigger"
          >
            <div className="flex flex-col space-y-1">
              <div className="flex items-start justify-between">
                <h3 className="text-sm font-medium text-white truncate flex-1 pr-2">
                  {conversation.title}
                </h3>
                <div className="relative" ref={openMenuId === conversation.id ? menuRef : null}>
                  <button
                    onClick={(e) => handleMenuToggle(e, conversation.id)}
                    className="opacity-0 group-hover:opacity-100 transition-all duration-200 text-gray-400 hover:text-white p-1 -m-1 hover:opacity-100 focus:opacity-100"
                    aria-label="Conversation options"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                    </svg>
                  </button>

                  {openMenuId === conversation.id && (
                    <div className="absolute right-0 top-8 z-10 min-w-48 animate-fade-in-only">
                      <div
                        className="py-1 rounded-lg border shadow-lg backdrop-blur-12px"
                        style={{
                          background: 'rgba(10, 10, 10, 0.85)',
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                          boxShadow:
                            '0 10px 20px 0 rgba(0, 0, 0, 0.4), inset 0 1px 0 0 rgba(255, 255, 255, 0.08)',
                        }}
                      >
                        <button
                          onClick={(e) => handleRenameClick(e, conversation)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-white transition-all duration-200 hover:bg-white/5"
                        >
                          Rename Conversation
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(e, conversation)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-red-400 transition-all duration-200 hover:bg-white/5"
                        >
                          Delete Conversation
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-400">{conversation.timeAgo}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={handleCancelDelete}
        closeOnBackdropClick={!isDeleting}
      >
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Delete Conversation</h2>
          <p className="text-gray-300">
            Are you sure you want to delete the conversation:
            <br />
            <span className="font-medium text-white mt-2 block">{deleteModal.title}</span>
          </p>
          <p className="text-sm text-gray-400">This action cannot be undone.</p>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="px-4 py-2 text-gray-400 hover:text-white transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-dark-secondary border border-red-600/50 text-red-400 rounded-lg hover:bg-dark-tertiary hover:border-red-500 hover:text-red-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Rename Conversation Modal */}
      <RenameConversationModal
        isOpen={renameModal.isOpen}
        onClose={handleCancelRename}
        currentTitle={renameModal.title}
        onRename={handleConfirmRename}
        isRenaming={isRenaming}
      />
    </>
  )
})

ConversationHistory.displayName = 'ConversationHistory'

ConversationHistory.propTypes = {
  onSelectConversation: PropTypes.func.isRequired,
}
